import { Component } from "@angular/core";
import { FormBuilder, ReactiveFormsModule, Validators } from "@angular/forms";
import { firstValueFrom } from "rxjs";

import { JslibModule } from "@bitwarden/angular/jslib.module";
import { ApiService } from "@bitwarden/common/abstractions/api.service";
import { AccountService } from "@bitwarden/common/auth/abstractions/account.service";
import { getUserId } from "@bitwarden/common/auth/services/account.service";
import { PreloginRequest } from "@bitwarden/common/models/request/prelogin.request";
import { I18nService } from "@bitwarden/common/platform/abstractions/i18n.service";
import { PlatformUtilsService } from "@bitwarden/common/platform/abstractions/platform-utils.service";
import {
  DialogRef,
  AsyncActionsModule,
  ButtonModule,
  DialogModule,
  FormFieldModule,
  IconButtonModule,
  ToastService,
} from "@bitwarden/components";
import { KdfConfigService, KeyService } from "@bitwarden/key-management";

/**
 * Used to verify the user's Master Password for the "Master Password Re-prompt" feature only.
 * See UserVerificationComponent for any other situation where you need to verify the user's identity.
 */
@Component({
  standalone: true,
  selector: "vault-password-reprompt",
  imports: [
    JslibModule,
    AsyncActionsModule,
    ButtonModule,
    DialogModule,
    FormFieldModule,
    IconButtonModule,
    ReactiveFormsModule,
  ],
  templateUrl: "password-reprompt.component.html",
})
export class PasswordRepromptComponent {
  formGroup = this.formBuilder.group({
    masterPassword: ["", { validators: [Validators.required], updateOn: "submit" }],
  });

  constructor(
    protected keyService: KeyService,
    protected platformUtilsService: PlatformUtilsService,
    protected i18nService: I18nService,
    protected formBuilder: FormBuilder,
    protected dialogRef: DialogRef,
    private toastService: ToastService,
    protected accountService: AccountService,
    private apiService: ApiService,
    private kdfConfigService: KdfConfigService,
  ) {}

  submit = async () => {
    // Exit early when a master password is not provided.
    // The form field required error will be shown to users in these cases.
    if (!this.formGroup.value.masterPassword) {
      return;
    }

    const userId = await firstValueFrom(getUserId(this.accountService.activeAccount$));

    if (userId == null) {
      throw new Error("An active user is expected while doing password reprompt.");
    }

    // Fyin pepper-strengthened PIN: derive the master key explicitly with the
    // peppered KDF input so it matches what peppered login stored. compareKeyHash
    // hashes the RAW PIN against this key (PasswordLoginStrategy convention), so
    // the local hash only matches when the key was derived from `pin || pepper`.
    // We deliberately do NOT use getOrDeriveMasterKey: on a cache miss (after a
    // lock/unlock, MASTER_KEY clears on lock) it derives from the raw PIN, so the
    // reprompt would wrongly reject a migrated user. Deriving explicitly is
    // correct whether or not the key is cached.
    const masterPassword = this.formGroup.value.masterPassword;
    const account = await firstValueFrom(this.accountService.activeAccount$);
    const email = account?.email;
    const fyinPepper = await this.fyinFetchPepperIfMigrated(email);
    const passwordForKdf = fyinPepper != null ? masterPassword + fyinPepper : masterPassword;
    const kdfConfig = await this.kdfConfigService.getKdfConfig(userId);
    const storedMasterKey = await this.keyService.makeMasterKey(passwordForKdf, email, kdfConfig);
    if (!(await this.keyService.compareKeyHash(masterPassword, storedMasterKey, userId))) {
      this.toastService.showToast({
        variant: "error",
        title: this.i18nService.t("errorOccurred"),
        message: this.i18nService.t("invalidMasterPassword"),
      });
      return;
    }

    this.dialogRef.close(true);
  };

  /**
   * Fyin pepper-strengthened PIN: returns the pepper string for a migrated user
   * (so callers can derive MK from `pin || pepper` rather than `pin` alone).
   * Returns null for unmigrated users / errors / pepper-disabled servers; callers
   * fall back to the legacy KDF in that case.
   */
  private async fyinFetchPepperIfMigrated(email: string): Promise<string | null> {
    try {
      const preloginResponse = await this.apiService.postPrelogin(new PreloginRequest(email));
      if (preloginResponse?.pepperRequired === true && typeof preloginResponse.pepper === "string") {
        return preloginResponse.pepper;
      }
    } catch (e) {
      // 404 / network / etc — fall back to legacy KDF
    }
    return null;
  }
}
